import {
  INSIGHT_POSTS,
  INSIGHTS_SITE_URL,
  getPublishedInsights,
} from "../data/insights";
import { normalizeInsightSections, slugify } from "../components/insights/insight-utils";

const STORAGE_KEY = "avanicimcon.insights.overrides.v1";

const canUseStorage = () => typeof window !== "undefined" && !!window.localStorage;

const readOverrides = () => {
  if (!canUseStorage()) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
};

const writeOverrides = (items) => {
  if (!canUseStorage()) return items;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  return items;
};

const mergeById = (seed, overrides) => {
  const map = new Map(seed.map((item) => [item.id, { ...item }]));
  overrides.forEach((item) => {
    map.set(item.id, { ...map.get(item.id), ...item });
  });
  return [...map.values()].sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
};

const resolveInsight = (payload, existing = {}) => {
  const title = payload.title || existing.title || "Untitled insight";
  const slug = payload.slug || existing.slug || slugify(title);
  const now = new Date().toISOString().slice(0, 10);
  const bodySections = normalizeInsightSections(payload.bodySections || existing.bodySections || []);

  return {
    ...existing,
    ...payload,
    slug,
    title,
    bodySections,
    tags: Array.isArray(payload.tags) ? payload.tags : existing.tags || [],
    keywords: Array.isArray(payload.keywords) ? payload.keywords : existing.keywords || [],
    relatedServices: Array.isArray(payload.relatedServices) ? payload.relatedServices : existing.relatedServices || [],
    relatedIndustries: Array.isArray(payload.relatedIndustries) ? payload.relatedIndustries : existing.relatedIndustries || [],
    relatedProducts: Array.isArray(payload.relatedProducts) ? payload.relatedProducts : existing.relatedProducts || [],
    relatedPosts: Array.isArray(payload.relatedPosts) ? payload.relatedPosts : existing.relatedPosts || [],
    featured: typeof payload.featured === "boolean" ? payload.featured : !!existing.featured,
    featuredRank: Number.isFinite(Number(payload.featuredRank))
      ? Number(payload.featuredRank)
      : Number.isFinite(Number(existing.featuredRank))
        ? Number(existing.featuredRank)
        : 999,
    status: payload.status || existing.status || "draft",
    updatedDate: payload.updatedDate || now,
    publishDate: payload.publishDate || existing.publishDate || now,
    readingTime: payload.readingTime || existing.readingTime || "5 min read",
    heroImage: payload.heroImage || existing.heroImage || "/images/hero-bridge.svg",
    heroImageAlt: payload.heroImageAlt || existing.heroImageAlt || title,
    authorName: payload.authorName || existing.authorName || "Avani Cimcon Editorial Team",
    authorTitle: payload.authorTitle || existing.authorTitle || "Research and Strategy",
    canonicalUrl: payload.canonicalUrl || existing.canonicalUrl || `https://www.avanicimcon.com/blog/${slug}`,
    metaTitle: payload.metaTitle || existing.metaTitle || `${title} | Avani Cimcon Insights`,
    metaDescription: payload.metaDescription || existing.metaDescription || existing.excerpt || "",
    ogTitle: payload.ogTitle || existing.ogTitle || title,
    ogDescription: payload.ogDescription || existing.ogDescription || existing.excerpt || "",
    twitterTitle: payload.twitterTitle || existing.twitterTitle || title,
    twitterDescription: payload.twitterDescription || existing.twitterDescription || existing.excerpt || "",
    ctaTitle: payload.ctaTitle || existing.ctaTitle || "Talk to Avani Cimcon",
    ctaText: payload.ctaText || existing.ctaText || "Discuss your roadmap with our team.",
    ctaButtonText: payload.ctaButtonText || existing.ctaButtonText || "Contact Us",
    ctaButtonUrl: payload.ctaButtonUrl || existing.ctaButtonUrl || "/contact-us",
    ogImage: payload.ogImage || existing.ogImage || "https://www.avanicimcon.com/images/og-homepage.jpg",
    twitterImage: payload.twitterImage || existing.twitterImage || payload.ogImage || existing.ogImage || "https://www.avanicimcon.com/images/og-homepage.jpg",
  };
};

export const listInsights = () => mergeById(INSIGHT_POSTS, readOverrides());

export { INSIGHTS_SITE_URL };

export const listPublishedInsights = () => listInsights().filter((post) => post.status === "published");

export const getInsightRecordById = (id) => listInsights().find((post) => post.id === id);

export const getInsightRecordBySlug = (slug) => listInsights().find((post) => post.slug === slug);

export const createInsight = (payload = {}) => {
  const record = resolveInsight({
    ...payload,
    id: payload.id || `custom-${Date.now()}`,
    status: payload.status || "draft",
    publishDate: payload.publishDate || new Date().toISOString().slice(0, 10),
    updatedDate: new Date().toISOString().slice(0, 10),
    featured: !!payload.featured,
    featuredRank: payload.featuredRank || 999,
    bodySections: payload.bodySections || [],
  });
  const overrides = [...readOverrides().filter((item) => item.id !== record.id), record];
  writeOverrides(overrides);
  return record;
};

export const updateInsight = (id, payload = {}) => {
  const existing = getInsightRecordById(id);
  if (!existing) return createInsight({ ...payload, id });
  const record = resolveInsight(
    {
      ...payload,
      id,
      updatedDate: new Date().toISOString().slice(0, 10),
      bodySections: payload.bodySections || existing.bodySections,
    },
    existing
  );
  const overrides = [...readOverrides().filter((item) => item.id !== id), record];
  writeOverrides(overrides);
  return record;
};

export const publishInsight = (id) => updateInsight(id, { status: "published" });

export const unpublishInsight = (id) => updateInsight(id, { status: "draft" });

export const archiveInsight = (id) => updateInsight(id, { status: "archived" });

export const resetInsightOverrides = () => writeOverrides([]);

export const getPublicInsightBySlug = (slug) => getPublishedInsights().find((post) => post.slug === slug);

export const getPublicInsights = () => getPublishedInsights();
